import { useEffect, useMemo, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import {
	StyledArrow,
	StyledContainer,
	StyledDropdown,
	StyledDropdownItem,
	StyledDropdownList,
	StyledInputWrapper,
	StyledSearchInput,
	StyledSearchWrapper,
} from './DonateItemDropdown.styled';
import { useDonateItemDropdown } from './useDonateItemDropdown';

interface DonateItemSelectOption {
	value: string;
	label: string;
}

interface DonateItemSelectProps {
	options: DonateItemSelectOption[];
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	filterPlaceholder?: string;
	showOptionValue?: boolean;
}

const DonateItemSelect: React.FC<DonateItemSelectProps> = ({
	options,
	value,
	onChange,
	placeholder = 'Выберите...',
	filterPlaceholder = 'Поиск...',
	showOptionValue = true,
}) => {
	const [debouncedFilter, setDebouncedFilter] = useState('');
	const {
		isOpen,
		filterText,
		filteredOptions: rawFilteredOptions,
		containerRef,
		inputRef,
		listRef,
		selectedOption,
		handleToggle,
		handleSelect,
		handleInputChange,
		handleInputKeyDown,
	} = useDonateItemDropdown(options, value, onChange);

	useEffect(() => {
		const timeoutId = setTimeout(() => setDebouncedFilter(filterText), 200);
		return () => clearTimeout(timeoutId);
	}, [filterText]);

	const filteredOptions = useMemo(() => {
		if (!debouncedFilter.trim()) return rawFilteredOptions;
		const lowerFilter = debouncedFilter.toLowerCase();
		return rawFilteredOptions.filter(
			(option) =>
				option.value.toLowerCase().includes(lowerFilter) ||
				option.label.toLowerCase().includes(lowerFilter),
		);
	}, [debouncedFilter, rawFilteredOptions]);

	useEffect(() => {
		if (isOpen && value && listRef.current) {
			const selectedElement = listRef.current.querySelector(
				'[data-selected="true"]',
			) as HTMLElement;
			selectedElement?.scrollIntoView({ block: 'center', behavior: 'smooth' });
		}
	}, [filteredOptions, isOpen, listRef, value]);

	const selectOption = (option: DonateItemSelectOption) => {
		playSound(Sound.LinkCursor);
		handleSelect(option);
	};

	return (
		<StyledContainer ref={containerRef}>
			<StyledInputWrapper
				$focus={isOpen}
				onClick={() => {
					playSound(Sound.LinkCursor);
					handleToggle();
				}}
			>
				<Text
					style={{
						flex: 1,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{selectedOption?.label || value || placeholder}
				</Text>
				<StyledArrow $active={isOpen}>▼</StyledArrow>
			</StyledInputWrapper>
			{isOpen && (
				<StyledDropdown>
					<StyledSearchWrapper>
						<StyledSearchInput
							ref={inputRef}
							value={filterText}
							onChange={handleInputChange}
							onKeyDown={handleInputKeyDown}
							placeholder={filterPlaceholder}
							autoFocus
						/>
					</StyledSearchWrapper>
					<StyledDropdownList ref={listRef}>
						{filteredOptions.length === 0 ? (
							<StyledDropdownItem disabled>
								<Text style={{ opacity: 0.5 }}>Ничего не найдено</Text>
							</StyledDropdownItem>
						) : (
							filteredOptions.map((option) => (
								<StyledDropdownItem
									key={option.value}
									$selected={option.value === value}
									data-selected={option.value === value}
									onClick={(event) => {
										event.stopPropagation();
										selectOption(option);
									}}
									onMouseEnter={() => playSound(Sound.LinkCursor)}
								>
									<Text>{option.label}</Text>
									{showOptionValue && option.value && (
										<Text style={{ opacity: 0.5, fontSize: 12, marginLeft: 8 }}>
											({option.value})
										</Text>
									)}
								</StyledDropdownItem>
							))
						)}
					</StyledDropdownList>
				</StyledDropdown>
			)}
		</StyledContainer>
	);
};

export type { DonateItemSelectOption };
export { DonateItemSelect };
